import type { EngagementChatAttachment } from '@houzy/schemas/partner-cockpit/chat';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { attachmentExtension } from '../utils/attachmentExtension';
import { isImageAttachment } from '../utils/isImageAttachment';

export type ChatPendingAttachmentsProps = {
    attachments: EngagementChatAttachment[];
    isUploading: boolean;
    onRemove: (attachmentUuid: string) => void;
};

const MINIATURE_SIZE = 72;

const MINIATURE_SX = {
    position: 'relative',
    width: MINIATURE_SIZE,
    height: MINIATURE_SIZE,
    flexShrink: 0,
    borderRadius: 1.5,
    border: 1,
    borderColor: 'divider',
    bgcolor: 'action.hover',
    overflow: 'hidden',
} as const;

// `display` stays out of MINIATURE_SX: the spinner tile is a Stack and needs to keep its flex.
const MINIATURE_LINK_SX = {
    ...MINIATURE_SX,
    display: 'block',
    '&:hover': { borderColor: 'primary.main' },
} as const;

function PendingAttachmentMiniature({
    attachment,
    onRemove,
}: {
    attachment: EngagementChatAttachment;
    onRemove: (attachmentUuid: string) => void;
}): ReactElement {
    const { t } = useTranslation();
    return (
        <Box sx={{ position: 'relative', pt: 1, pr: 1 }}>
            <Box
                component="a"
                href={attachment.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={MINIATURE_LINK_SX}
                title={attachment.displayName}
                aria-label={attachment.displayName}>
                {isImageAttachment(attachment) ? (
                    <Box
                        component="img"
                        src={attachment.url}
                        alt={attachment.displayName}
                        sx={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                ) : (
                    <Stack alignItems="center" justifyContent="center" spacing={0.25} sx={{ height: '100%', px: 0.5 }}>
                        <InsertDriveFileOutlinedIcon fontSize="small" color="action" />
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            sx={{ maxWidth: '100%', textTransform: 'uppercase' }}>
                            {attachmentExtension(attachment.displayName)}
                        </Typography>
                    </Stack>
                )}
            </Box>
            <IconButton
                size="small"
                aria-label={t('OFFER_REQUESTS.CHAT.REMOVE_ATTACHMENT')}
                onClick={() => onRemove(attachment.uuid)}
                sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    p: 0.25,
                    bgcolor: 'background.paper',
                    border: 1,
                    borderColor: 'divider',
                    '&:hover': { bgcolor: 'background.paper' },
                }}>
                <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
        </Box>
    );
}

export function ChatPendingAttachments({
    attachments,
    isUploading,
    onRemove,
}: ChatPendingAttachmentsProps): ReactElement | null {
    const { t } = useTranslation();
    if (attachments.length === 0 && !isUploading) {
        return null;
    }

    return (
        <Stack spacing={0.5}>
            <Typography variant="caption" color="text.secondary">
                {isUploading
                    ? t('OFFER_REQUESTS.CHAT.UPLOADING_ATTACHMENTS')
                    : t('OFFER_REQUESTS.CHAT.PENDING_ATTACHMENTS_HINT')}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap" useFlexGap>
                {attachments.map((attachment) => (
                    <PendingAttachmentMiniature key={attachment.uuid} attachment={attachment} onRemove={onRemove} />
                ))}
                {isUploading && (
                    <Box sx={{ pt: 1, pr: 1 }}>
                        <Stack sx={MINIATURE_SX} alignItems="center" justifyContent="center">
                            <CircularProgress size={20} aria-label={t('OFFER_REQUESTS.CHAT.UPLOADING_ATTACHMENTS')} />
                        </Stack>
                    </Box>
                )}
            </Stack>
        </Stack>
    );
}
